import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * FireCodeWorkflowViewer
 * ----------------------
 * A self-contained, data-driven viewer for the SCDF Code of Practice for Fire
 * Precautions in Buildings 2023 — built for FireSafetySingapore.com (Readdy.ai).
 *
 * Features
 *   1. Chapter side-navigation (collapsible on mobile)
 *   2. Global clause-reference / keyword search bar (jumps to matching sections)
 *   3. Interactive decision-logic tree: click through nodes until a compliance
 *      outcome is reached, with a breadcrumb trail, back/restart controls and a
 *      highlighted compliance result panel.
 *   4. DEEP-LINKING — every chapter, section, clause, search and individual
 *      compliance RESULT has its own shareable URL. Users can copy a link to a
 *      specific outcome and it will replay the decision path on load.
 *
 * URL scheme (relative to `basePath`, default "" = hash routing):
 *   /chapter/:n
 *   /chapter/:n/:sectionSlug
 *   /chapter/:n/:sectionSlug/result/:nodeId   ← shareable compliance result
 *   /clause/:clauseNumber                     ← resolves to the owning section
 *   /search/:query
 *
 * Routing modes
 *   • Hash mode (default): URLs look like  https://site/page#/chapter/6/...
 *     Zero server config — works on any static host and inside Readdy.ai preview.
 *   • Path mode: pass `basePath="/fire-code"` AND a `router` adapter (see props)
 *     to get clean, crawlable URLs like https://site/fire-code/chapter/6/...
 *     for section-level SEO. See INTEGRATION.md.
 *
 * Data
 *   Pass an array of chapter objects via the `chapters` prop. Each object must
 *   conform to workflow.schema.json. See ./data/index.js for the bundled dataset.
 *
 * Styling
 *   Tailwind utility classes only (Readdy.ai-native). React is the only runtime dep.
 */

/* ------------------------------------------------------------------ */
/* Style tokens                                                        */
/* ------------------------------------------------------------------ */
const SEVERITY_STYLES = {
  critical: { dot: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200", label: "Critical" },
  important: { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Important" },
  note: { dot: "bg-sky-500", chip: "bg-sky-50 text-sky-700 border-sky-200", label: "Note" },
};

const NODE_STYLES = {
  start: { box: "border-slate-300 bg-slate-50", badge: "bg-slate-600 text-white", label: "Start" },
  decision: { box: "border-indigo-300 bg-indigo-50", badge: "bg-indigo-600 text-white", label: "Decision" },
  action: { box: "border-emerald-300 bg-emerald-50", badge: "bg-emerald-600 text-white", label: "Action" },
  outcome: { box: "border-emerald-500 bg-emerald-100", badge: "bg-emerald-700 text-white", label: "Outcome" },
  reference: { box: "border-slate-300 bg-white", badge: "bg-slate-500 text-white", label: "Reference" },
};

/* ================================================================== */
/* Clause + slug helpers (also exported for prerender/sitemap use)     */
/* ================================================================== */

/**
 * Normalise any clause string/query to a canonical comparable form so that
 * "Clause 6.4.1", "Cl.6.4.1", "cl 6.4.1" and "6.4.1" all match each other.
 */
export function normaliseClause(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/clause|cl\.?/g, " ")
    .replace(/[^a-z0-9]/g, "");
}

/** Extract just the clause NUMBER (e.g. "6.4.1", "3.11.5a") from any clause string. */
export function clauseNumber(str) {
  const m = String(str || "").match(/(\d+(?:\.\d+)*[a-z]?(?:\([0-9a-z]+\))*)/i);
  return m ? m[1] : "";
}

/** Does the query look like a clause number? */
function looksLikeClause(q) {
  return /^\s*(?:clause|cl\.?)?\s*\d/i.test(q) && /\d/.test(q);
}

/** Stable slug for a section: prefer the data `id`, else slugify the title. */
export function sectionSlug(section) {
  if (section?.id) return String(section.id);
  return String(section?.title || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Build a flat index of the dataset for routing/SEO:
 *   { sections: [{chapter, chapterTitle, slug, section}], clauseToSection: Map }
 */
export function buildIndex(chapters) {
  const sections = [];
  const clauseToSection = new Map();
  (chapters || []).forEach((c) => {
    (c.sections || []).forEach((s) => {
      const slug = sectionSlug(s);
      const entry = { chapter: c.chapter, chapterTitle: c.title, slug, section: s };
      sections.push(entry);
      // register every clause ref that appears in this section
      const refs = [
        s.clauseRef,
        ...(s.criticalPoints || []).map((p) => p.clauseRef),
        ...(s.thresholds || []).map((t) => t.clauseRef),
        ...((s.workflow?.nodes) || []).map((n) => n.clauseRef),
      ].filter(Boolean);
      refs.forEach((r) => {
        const key = normaliseClause(clauseNumber(r) || r);
        if (key && !clauseToSection.has(key)) clauseToSection.set(key, entry);
      });
    });
  });
  return { sections, clauseToSection };
}

/* ================================================================== */
/* Routing                                                             */
/* ================================================================== */

/**
 * Parse a route string (the part AFTER basePath, no leading slash) into a
 * structured route object. Falls back to { view: "chapter", chapter: <first> }.
 */
function parseRoute(routeStr) {
  const clean = String(routeStr || "").replace(/^#?\/?/, "").replace(/\/+$/, "");
  const parts = clean.split("/").filter(Boolean).map(decodeURIComponent);
  if (parts.length === 0) return { view: "home" };

  if (parts[0] === "search") {
    return { view: "search", query: parts.slice(1).join("/") };
  }
  if (parts[0] === "clause") {
    return { view: "clause", clause: parts[1] || "" };
  }
  if (parts[0] === "chapter") {
    const chapter = parseInt(parts[1], 10);
    if (Number.isNaN(chapter)) return { view: "home" };
    // /chapter/:n/:slug/result/:nodeId
    if (parts[3] === "result" && parts[4]) {
      return { view: "result", chapter, slug: parts[2], nodeId: parts[4] };
    }
    if (parts[2]) return { view: "section", chapter, slug: parts[2] };
    return { view: "chapter", chapter };
  }
  return { view: "home" };
}

/** Build a route string (without basePath, with a leading slash). */
function buildRoute(route) {
  switch (route.view) {
    case "search":
      return `/search/${encodeURIComponent(route.query)}`;
    case "clause":
      return `/clause/${encodeURIComponent(route.clause)}`;
    case "result":
      return `/chapter/${route.chapter}/${encodeURIComponent(route.slug)}/result/${encodeURIComponent(route.nodeId)}`;
    case "section":
      return `/chapter/${route.chapter}/${encodeURIComponent(route.slug)}`;
    case "chapter":
      return `/chapter/${route.chapter}`;
    default:
      return "/";
  }
}

/**
 * Routing hook. Default mode = hash routing (no deps, works anywhere).
 * Pass a `router` adapter { getPath, push, subscribe } to use path-based
 * routing (clean URLs) — see INTEGRATION.md for Next.js / React Router adapters.
 */
function useRouter({ basePath = "", router = null }) {
  const readHash = useCallback(() => {
    if (typeof window === "undefined") return "/";
    return window.location.hash.replace(/^#/, "") || "/";
  }, []);

  const readPath = useCallback(() => {
    if (router) return router.getPath();
    if (typeof window === "undefined") return "/";
    let p = window.location.pathname;
    if (basePath && p.startsWith(basePath)) p = p.slice(basePath.length);
    return p || "/";
  }, [basePath, router]);

  const usePath = !!router || !!basePath;
  const [routeStr, setRouteStr] = useState(() => (usePath ? readPath() : readHash()));

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const update = () => setRouteStr(usePath ? readPath() : readHash());
    if (router?.subscribe) {
      const unsub = router.subscribe(update);
      return unsub;
    }
    const evt = usePath ? "popstate" : "hashchange";
    window.addEventListener(evt, update);
    return () => window.removeEventListener(evt, update);
  }, [usePath, router, readHash, readPath]);

  const navigate = useCallback(
    (route, { replace = false } = {}) => {
      const rel = buildRoute(route);
      if (usePath) {
        const full = (basePath || "") + rel;
        if (router) router.push(full, { replace });
        else if (typeof window !== "undefined") {
          window.history[replace ? "replaceState" : "pushState"]({}, "", full);
        }
        setRouteStr(rel);
      } else {
        if (typeof window !== "undefined") {
          const url = "#" + rel;
          if (replace) window.history.replaceState({}, "", url);
          else window.location.hash = rel;
        }
        setRouteStr(rel);
      }
    },
    [usePath, basePath, router]
  );

  /** Absolute, shareable URL for a route (for Copy-link buttons). */
  const hrefFor = useCallback(
    (route) => {
      const rel = buildRoute(route);
      if (typeof window === "undefined") return rel;
      const { origin, pathname } = window.location;
      if (usePath) return origin + (basePath || "") + rel;
      return origin + pathname + "#" + rel;
    },
    [usePath, basePath]
  );

  return { route: parseRoute(routeStr), navigate, hrefFor };
}

/* ================================================================== */
/* Decision-tree path solver (for shareable results)                  */
/* ================================================================== */

/**
 * BFS from workflow.start to a target node id, returning the path as a list of
 * { id, choiceLabel } where choiceLabel is the branch taken to leave that node.
 * Used to REPLAY a shared result URL. Returns null if unreachable.
 */
function solvePathTo(workflow, targetId) {
  if (!workflow?.start || !targetId) return null;
  const nodeMap = {};
  (workflow.nodes || []).forEach((n) => (nodeMap[n.id] = n));
  if (!nodeMap[targetId]) return null;

  const queue = [[{ id: workflow.start, choiceLabel: null }]];
  const seen = new Set([workflow.start]);
  while (queue.length) {
    const path = queue.shift();
    const last = path[path.length - 1];
    if (last.id === targetId) return path;
    const node = nodeMap[last.id];
    if (!node) continue;
    const edges = [];
    if (node.type === "decision" && node.branches) {
      node.branches.forEach((b) => edges.push({ target: b.target, label: b.label }));
    } else if (node.next) {
      edges.push({ target: node.next, label: "Continue" });
    }
    for (const e of edges) {
      if (!nodeMap[e.target] || seen.has(e.target)) continue;
      seen.add(e.target);
      const nextPath = path.slice();
      nextPath[nextPath.length - 1] = { ...last, choiceLabel: e.label };
      nextPath.push({ id: e.target, choiceLabel: null });
      queue.push(nextPath);
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Small UI atoms                                                      */
/* ------------------------------------------------------------------ */
function ClauseTag({ clauseRef }) {
  if (!clauseRef) return null;
  return (
    <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200">
      {clauseRef}
    </span>
  );
}

function NodeBadge({ type }) {
  const s = NODE_STYLES[type] || NODE_STYLES.reference;
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${s.badge}`}>
      {s.label}
    </span>
  );
}

/** A small "Copy link" button that copies an absolute URL to the clipboard. */
function CopyLinkButton({ href, label = "Copy link", className = "" }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) await navigator.clipboard.writeText(href);
      else {
        const ta = document.createElement("textarea");
        ta.value = href;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title={href}
      className={`inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium transition ${
        copied ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "text-slate-500 hover:bg-slate-50"
      } ${className}`}
    >
      {copied ? "✓ Copied" : `🔗 ${label}`}
    </button>
  );
}

/* Chapter flow-diagram panel — expandable, with a full-screen lightbox. */
function ChapterDiagram({ diagram, chapterNum }) {
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(false);
  if (!diagram?.src) return null;
  return (
    <div className="mt-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-red-300 hover:bg-red-50"
      >
        <span className="text-red-600">▣</span>
        {open ? "Hide flow diagram" : "View flow diagram"}
      </button>
      {open && (
        <figure className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setZoom(true)}
            className="block w-full cursor-zoom-in"
            aria-label="Enlarge flow diagram"
          >
            <img
              src={diagram.src}
              alt={`Chapter ${chapterNum} compliance flow diagram — ${diagram.caption || ""}`}
              loading="lazy"
              className="w-full"
            />
          </button>
          {diagram.caption && (
            <figcaption className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
              Flow diagram · {diagram.caption} — click to enlarge
            </figcaption>
          )}
        </figure>
      )}
      {zoom && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 p-4"
          onClick={() => setZoom(false)}
        >
          <button
            className="absolute right-4 top-4 rounded-md bg-white/90 px-3 py-1.5 text-sm font-medium text-slate-700"
            onClick={() => setZoom(false)}
          >
            ✕ Close
          </button>
          <img
            src={diagram.src}
            alt={`Chapter ${chapterNum} compliance flow diagram (enlarged)`}
            className="max-h-full max-w-full overflow-auto rounded-lg bg-white"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

/* Highlight matched search text inside a string */
function Highlight({ text, query }) {
  if (!query) return <>{text}</>;
  const q = query.trim();
  if (!q) return <>{text}</>;
  try {
    const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
    const parts = String(text).split(re);
    return (
      <>
        {parts.map((p, i) =>
          re.test(p) ? (
            <mark key={i} className="rounded bg-yellow-200 px-0.5 text-slate-900">
              {p}
            </mark>
          ) : (
            <span key={i}>{p}</span>
          )
        )}
      </>
    );
  } catch {
    return <>{text}</>;
  }
}

/* ------------------------------------------------------------------ */
/* Interactive decision-logic tree                                     */
/* ------------------------------------------------------------------ */
function DecisionTree({ workflow, sectionRoute, resultNodeId, hrefFor, onReachOutcome }) {
  const nodeMap = useMemo(() => {
    const m = {};
    (workflow?.nodes || []).forEach((n) => (m[n.id] = n));
    return m;
  }, [workflow]);

  const initialPath = useCallback(() => {
    // If a result node was requested via URL, replay the path to it.
    if (resultNodeId && nodeMap[resultNodeId]) {
      const solved = solvePathTo(workflow, resultNodeId);
      if (solved) return solved;
    }
    return workflow?.start ? [{ id: workflow.start, choiceLabel: null }] : [];
  }, [workflow, resultNodeId, nodeMap]);

  const [path, setPath] = useState(initialPath);

  // Re-sync when workflow or the requested result node changes.
  useEffect(() => {
    setPath(initialPath());
  }, [initialPath]);

  const current = nodeMap[path[path.length - 1]?.id];

  // Notify parent when we land on an outcome so it can update the URL.
  useEffect(() => {
    if (current && current.type === "outcome" && onReachOutcome) {
      onReachOutcome(current.id);
    }
  }, [current, onReachOutcome]);

  if (!current) return null;

  const advance = (targetId, choiceLabel) => {
    if (!targetId || !nodeMap[targetId]) return;
    setPath((p) => {
      const next = [...p];
      next[next.length - 1] = { ...next[next.length - 1], choiceLabel };
      next.push({ id: targetId, choiceLabel: null });
      return next;
    });
  };

  const restart = () => setPath(workflow?.start ? [{ id: workflow.start, choiceLabel: null }] : []);
  const back = () => setPath((p) => (p.length > 1 ? p.slice(0, -1) : p));

  const isOutcome = current.type === "outcome";
  const s = NODE_STYLES[current.type] || NODE_STYLES.reference;
  const resultHref =
    isOutcome && sectionRoute && hrefFor
      ? hrefFor({ view: "result", chapter: sectionRoute.chapter, slug: sectionRoute.slug, nodeId: current.id })
      : null;

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-700">Interactive decision tree</h4>
        <div className="flex gap-2">
          <button
            onClick={back}
            disabled={path.length <= 1}
            className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ← Back
          </button>
          <button
            onClick={restart}
            className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            Restart
          </button>
        </div>
      </div>

      {/* breadcrumb trail of answered steps */}
      {path.length > 1 && (
        <ol className="mb-4 space-y-2 border-l-2 border-slate-100 pl-3">
          {path.slice(0, -1).map((step, i) => {
            const n = nodeMap[step.id];
            return (
              <li key={`${step.id}-${i}`} className="relative text-xs">
                <span className="absolute -left-[18px] top-1 h-2 w-2 rounded-full bg-slate-300" />
                <span className="text-slate-500">{n?.text}</span>
                {step.choiceLabel && (
                  <span className="ml-1 font-semibold text-indigo-600">→ {step.choiceLabel}</span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {/* current node */}
      <div className={`rounded-lg border p-4 ${s.box}`}>
        <div className="mb-2 flex items-center gap-2">
          <NodeBadge type={current.type} />
          <ClauseTag clauseRef={current.clauseRef} />
        </div>
        <p className={`text-sm leading-snug ${isOutcome ? "font-semibold text-emerald-900" : "font-medium text-slate-800"}`}>
          {current.text}
        </p>

        {/* decision branches */}
        {current.type === "decision" && current.branches && (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {current.branches.map((b, i) => (
              <button
                key={i}
                onClick={() => advance(b.target, b.label)}
                className="rounded-lg border border-indigo-300 bg-white px-3 py-2 text-left text-sm font-medium text-indigo-700 shadow-sm transition hover:bg-indigo-600 hover:text-white sm:text-center"
              >
                {b.label}
              </button>
            ))}
          </div>
        )}

        {/* continue button for linear nodes (start/action/reference) */}
        {current.type !== "decision" && current.type !== "outcome" && current.next && (
          <button
            onClick={() => advance(current.next, "Continue")}
            className="mt-3 inline-flex items-center rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-medium text-emerald-700 shadow-sm transition hover:bg-emerald-600 hover:text-white"
          >
            Continue →
          </button>
        )}
      </div>

      {/* compliance result panel */}
      {isOutcome && (
        <div className="mt-4 rounded-lg border-2 border-emerald-500 bg-emerald-50 p-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">
              ✓
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold uppercase tracking-wide text-emerald-800">Compliance result reached</p>
              <p className="mt-1 text-sm text-emerald-900">{current.text}</p>
              {current.clauseRef && (
                <p className="mt-2 text-xs text-emerald-700">
                  Authoritative clause: <span className="font-semibold">{current.clauseRef}</span> — verify against the full Fire Code text.
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  onClick={restart}
                  className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
                >
                  Run another check
                </button>
                {resultHref && <CopyLinkButton href={resultHref} label="Copy result link" />}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Critical points + thresholds                                        */
/* ------------------------------------------------------------------ */
function CriticalPoints({ points, query }) {
  if (!points || points.length === 0) return null;
  return (
    <div className="mt-4 space-y-2">
      <h4 className="text-sm font-semibold text-slate-700">Critical points</h4>
      <ul className="space-y-2">
        {points.map((p, i) => {
          const sv = SEVERITY_STYLES[p.severity] || SEVERITY_STYLES.important;
          return (
            <li key={i} className={`flex items-start gap-3 rounded-lg border p-3 ${sv.chip}`}>
              <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${sv.dot}`} />
              <div className="min-w-0">
                <p className="text-sm leading-snug">
                  <Highlight text={p.point} query={query} />
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">{sv.label}</span>
                  <ClauseTag clauseRef={p.clauseRef} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Thresholds({ thresholds, query }) {
  if (!thresholds || thresholds.length === 0) return null;
  return (
    <div className="mt-4">
      <h4 className="mb-2 text-sm font-semibold text-slate-700">Compliance thresholds</h4>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">Parameter</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">Value</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">Condition</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">Clause</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {thresholds.map((t, i) => (
              <tr key={i}>
                <td className="px-3 py-2 text-slate-700"><Highlight text={t.parameter} query={query} /></td>
                <td className="px-3 py-2 font-semibold text-slate-900">{t.value}</td>
                <td className="px-3 py-2 text-slate-500">{t.condition || "—"}</td>
                <td className="px-3 py-2 text-slate-500"><Highlight text={t.clauseRef || "—"} query={query} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section card                                                        */
/* ------------------------------------------------------------------ */
function SectionCard({
  chapterNum,
  slug,
  section,
  defaultOpen,
  query,
  registerRef,
  hrefFor,
  resultNodeId,
  onReachOutcome,
  onToggle,
}) {
  const [open, setOpen] = useState(!!defaultOpen);

  // Stay in sync when defaultOpen changes (e.g. deep-link opens this section).
  useEffect(() => {
    setOpen(!!defaultOpen);
  }, [defaultOpen]);

  // auto-open when it becomes a search hit
  useEffect(() => {
    if (query) setOpen(true);
  }, [query]);

  const sectionHref = hrefFor ? hrefFor({ view: "section", chapter: chapterNum, slug }) : null;
  const anchorId = `clause-${slug}`;

  const toggle = () => {
    setOpen((o) => {
      const nv = !o;
      if (nv && onToggle) onToggle(slug);
      return nv;
    });
  };

  return (
    <div
      id={anchorId}
      ref={registerRef}
      className="scroll-mt-24 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <div className="flex items-start justify-between gap-2 px-5 py-4">
        <button onClick={toggle} className="flex min-w-0 flex-1 items-start gap-4 text-left">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900">
                <Highlight text={section.title} query={query} />
              </h3>
              <ClauseTag clauseRef={section.clauseRef} />
            </div>
            <p className="mt-1 text-sm text-slate-500">
              <Highlight text={section.overview} query={query} />
            </p>
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          {sectionHref && <CopyLinkButton href={sectionHref} label="Link" />}
          <button
            onClick={toggle}
            aria-label={open ? "Collapse section" : "Expand section"}
            className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          >
            ▾
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-100 px-5 pb-5 pt-2">
          <CriticalPoints points={section.criticalPoints} query={query} />
          <Thresholds thresholds={section.thresholds} query={query} />
          {section.workflow && (
            <DecisionTree
              workflow={section.workflow}
              sectionRoute={{ chapter: chapterNum, slug }}
              resultNodeId={resultNodeId}
              hrefFor={hrefFor}
              onReachOutcome={onReachOutcome}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Search match helper                                                 */
/* ------------------------------------------------------------------ */
function sectionMatches(section, q) {
  if (!q) return true;
  const clauseRefs = [
    section.clauseRef,
    ...(section.criticalPoints || []).map((p) => p.clauseRef),
    ...(section.thresholds || []).map((t) => t.clauseRef),
    ...((section.workflow?.nodes) || []).map((n) => n.clauseRef),
  ].filter(Boolean);

  if (looksLikeClause(q)) {
    const nq = normaliseClause(q);
    if (nq && clauseRefs.some((c) => normaliseClause(c).includes(nq))) return true;
  }

  const hay = [
    section.title,
    section.overview,
    ...clauseRefs,
    ...(section.criticalPoints || []).map((p) => p.point),
    ...(section.thresholds || []).flatMap((t) => [t.parameter, t.condition]),
    ...((section.workflow?.nodes) || []).map((n) => n.text),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q.toLowerCase());
}

/* ------------------------------------------------------------------ */
/* Main viewer                                                         */
/* ------------------------------------------------------------------ */
export default function FireCodeWorkflowViewer({ chapters = [], diagrams = null, basePath = "", router = null, onRouteChange }) {
  const index = useMemo(() => buildIndex(chapters), [chapters]);
  const { route, navigate, hrefFor } = useRouter({ basePath, router });

  const [navOpen, setNavOpen] = useState(false);
  const sectionRefs = useRef({});

  // ---- Resolve the route into concrete view state ----
  const firstChapter = chapters[0]?.chapter ?? 1;

  // A clause route resolves to its owning section, then we redirect.
  useEffect(() => {
    if (route.view === "clause") {
      const key = normaliseClause(clauseNumber(route.clause) || route.clause);
      const entry = index.clauseToSection.get(key);
      if (entry) {
        navigate({ view: "section", chapter: entry.chapter, slug: entry.slug }, { replace: true });
      } else {
        navigate({ view: "chapter", chapter: firstChapter }, { replace: true });
      }
    }
  }, [route, index, navigate, firstChapter]);

  // Notify host app of route changes (for <title>/meta updates, analytics).
  useEffect(() => {
    if (onRouteChange) onRouteChange(route);
  }, [route, onRouteChange]);

  const activeChapter =
    route.view === "chapter" || route.view === "section" || route.view === "result"
      ? route.chapter
      : firstChapter;

  const query = route.view === "search" ? route.query || "" : "";

  const current = useMemo(
    () => chapters.find((c) => c.chapter === activeChapter) || chapters[0],
    [chapters, activeChapter]
  );

  const openSlug = route.view === "section" || route.view === "result" ? route.slug : null;
  const resultNodeId = route.view === "result" ? route.nodeId : null;

  // ---- Search ----
  const searchHits = useMemo(() => {
    const q = (query || "").trim();
    if (!q) return [];
    const hits = [];
    chapters.forEach((c) => {
      (c.sections || []).forEach((s) => {
        if (sectionMatches(s, q)) hits.push({ chapter: c.chapter, chapterTitle: c.title, slug: sectionSlug(s), section: s });
      });
    });
    return hits;
  }, [chapters, query]);

  // ---- Scroll to a deep-linked / opened section ----
  useEffect(() => {
    if ((route.view === "section" || route.view === "result") && route.slug) {
      const t = setTimeout(() => {
        const el = sectionRefs.current[route.slug];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [route.view, route.slug, route.chapter]);

  // ---- Navigation handlers ----
  const goChapter = (n) => {
    navigate({ view: "chapter", chapter: n });
    setNavOpen(false);
  };
  const goSection = (chapter, slug) => {
    navigate({ view: "section", chapter, slug });
    setNavOpen(false);
  };
  const setQuery = (val) => {
    const v = (val || "").trim();
    if (v) navigate({ view: "search", query: v }, { replace: true });
    else navigate({ view: "chapter", chapter: activeChapter }, { replace: true });
  };

  // When a decision tree lands on an outcome through user clicks, reflect it in the URL.
  const handleReachOutcome = useCallback(
    (nodeId) => {
      if (route.view === "result" && route.nodeId === nodeId) return; // already there
      if (!openSlug) return;
      navigate(
        { view: "result", chapter: activeChapter, slug: openSlug, nodeId },
        { replace: true }
      );
    },
    [route, openSlug, activeChapter, navigate]
  );

  if (!chapters.length) {
    return <div className="p-8 text-center text-slate-500">No chapter data provided.</div>;
  }

  const inSearch = !!(query && query.trim());

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3">
          <button
            onClick={() => setNavOpen((o) => !o)}
            className="rounded-md border border-slate-200 px-2.5 py-1.5 text-sm font-medium text-slate-600 lg:hidden"
            aria-label="Toggle chapter navigation"
          >
            ☰
          </button>
          <button onClick={() => goChapter(firstChapter)} className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-red-600 text-sm font-bold text-white">F</span>
            <span className="text-sm font-bold tracking-tight text-slate-900">
              SCDF Fire Code 2023 <span className="font-normal text-slate-400">· Compliance Workflows</span>
            </span>
          </button>

          {/* Search bar */}
          <div className="relative ml-auto w-full max-w-md">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clause refs or keywords (e.g. 'Cl.6.4.1', 'sprinkler', 'travel distance')"
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-8 text-sm shadow-sm outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-100"
            />
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">⌕</span>
            {inSearch && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-1 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl">
        {/* Side nav */}
        <aside
          className={`fixed inset-y-0 left-0 z-40 w-72 transform overflow-y-auto border-r border-slate-200 bg-white px-3 py-4 transition-transform lg:static lg:z-0 lg:translate-x-0 ${
            navOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="mb-2 flex items-center justify-between px-2 lg:hidden">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Chapters</span>
            <button onClick={() => setNavOpen(false)} className="text-slate-400">✕</button>
          </div>
          <p className="mb-2 hidden px-2 text-xs font-semibold uppercase tracking-wide text-slate-400 lg:block">
            Chapters
          </p>
          <nav className="space-y-1">
            {chapters.map((c) => {
              const isActive = c.chapter === activeChapter && !inSearch;
              return (
                <button
                  key={c.chapter}
                  onClick={() => goChapter(c.chapter)}
                  className={`flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${
                    isActive ? "bg-red-50 text-red-700 ring-1 ring-red-100" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold ${
                      isActive ? "bg-red-600 text-white" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {c.chapter}
                  </span>
                  <span className="leading-snug">{c.title}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* mobile overlay */}
        {navOpen && (
          <div className="fixed inset-0 z-30 bg-slate-900/30 lg:hidden" onClick={() => setNavOpen(false)} />
        )}

        {/* Main pane */}
        <main className="min-w-0 flex-1 px-4 py-6 lg:px-8">
          {inSearch ? (
            /* Search results mode */
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">
                  {searchHits.length} match{searchHits.length === 1 ? "" : "es"} for “{query.trim()}”
                </h2>
                <button onClick={() => setQuery("")} className="text-sm font-medium text-red-600 hover:underline">
                  Clear
                </button>
              </div>
              {searchHits.length === 0 ? (
                <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                  No sections match. Try a clause number (e.g. “2.2”) or a keyword (e.g. “riser”, “compartment”).
                </p>
              ) : (
                <div className="space-y-3">
                  {searchHits.map(({ chapter, chapterTitle, slug, section }) => (
                    <button
                      key={`${chapter}-${section.id}`}
                      onClick={() => goSection(chapter, slug)}
                      className="block w-full rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-red-300 hover:shadow"
                    >
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700">
                          Ch {chapter}
                        </span>
                        <ClauseTag clauseRef={section.clauseRef} />
                      </div>
                      <h3 className="mt-1 text-sm font-semibold text-slate-900">
                        <Highlight text={section.title} query={query} />
                      </h3>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-500">
                        <Highlight text={section.overview} query={query} />
                      </p>
                      <p className="mt-2 text-xs text-slate-400">{chapterTitle}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            current && (
              <article className="mx-auto max-w-3xl">
                <header className="mb-6">
                  <p className="text-sm font-semibold uppercase tracking-wide text-red-600">
                    Chapter {current.chapter}
                  </p>
                  <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-3xl">{current.title}</h1>
                  <p className="mt-3 text-base leading-relaxed text-slate-600">{current.summary}</p>
                  {current.scope && (
                    <p className="mt-3 rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-600">
                      <span className="font-semibold text-slate-700">Scope: </span>
                      {current.scope}
                    </p>
                  )}
                  <p className="mt-3 text-xs text-slate-400">
                    Source: SCDF Code of Practice for Fire Precautions in Buildings 2023. This is a guidance summary —
                    always verify against the full Fire Code clause text.
                  </p>
                  {diagrams && (
                    <ChapterDiagram diagram={diagrams[current.chapter]} chapterNum={current.chapter} />
                  )}
                </header>

                <div className="space-y-3">
                  {(current.sections || []).map((s, i) => {
                    const slug = sectionSlug(s);
                    const isOpenTarget = openSlug ? slug === openSlug : i === 0;
                    return (
                      <SectionCard
                        key={s.id}
                        chapterNum={current.chapter}
                        slug={slug}
                        section={s}
                        defaultOpen={isOpenTarget}
                        query=""
                        hrefFor={hrefFor}
                        resultNodeId={slug === openSlug ? resultNodeId : null}
                        onReachOutcome={slug === openSlug ? handleReachOutcome : undefined}
                        onToggle={(sl) => goSection(current.chapter, sl)}
                        registerRef={(el) => (sectionRefs.current[slug] = el)}
                      />
                    );
                  })}
                </div>
              </article>
            )
          )}
        </main>
      </div>
    </div>
  );
}
